import React, { useState } from 'react';
import { 
  BookOpen, Calendar, Clock, Award, ChevronRight, CheckCircle2, 
  Sparkles, Layers, ArrowRight, X, GraduationCap 
} from 'lucide-react';
import { NairaSign } from './NairaSign';
import { DEPARTMENTS_AND_PROGRAMMES } from '../data/portalData';

export const ProgrammesSection = ({ setActiveView, onSelectProgramme }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProgrammeModal, setSelectedProgrammeModal] = useState(null);

  const resolveProgrammeId = (prog) => {
    if (!prog) return 'dba';
    const id = (prog.id || '').toLowerCase();
    if (id.includes('phd-ba') || (id.includes('phd') && !id.includes('af'))) return 'phd-ba';
    if (id.includes('dba')) return 'dba';
    if (id.includes('mba') || id.includes('emba')) return 'mba';
    if (id.includes('pgd')) return 'pgd-ba';
    if (id.includes('dpa')) return 'dpa';
    if (id.includes('phd-af') || id.includes('af') || id.includes('acc') || id.includes('fin')) return 'phd-af';
    return 'dba';
  };

  const handleOpenDedicatedPage = (prog) => {
    const targetId = resolveProgrammeId(prog);
    if (onSelectProgramme) {
      onSelectProgramme(targetId);
    } else {
      setActiveView('programme-detail');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Flatten all programmes with their department info
  const allProgrammes = DEPARTMENTS_AND_PROGRAMMES.flatMap(dept => 
    dept.programmes.map(prog => ({
      ...prog,
      departmentName: dept.name
    }))
  );

  const categories = ['All', 'Doctoral', "Master's", 'Postgraduate Diploma', 'Diploma', 'Certificate'];

  const filteredProgrammes = selectedCategory === 'All' 
    ? allProgrammes 
    : allProgrammes.filter(p => p.category === selectedCategory);

  return (
    <section id="programmes" className="py-20 bg-slate-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-amber-50 px-3 py-1 rounded-lg border border-[#7A470E]/40 mb-3 font-sc">
            <Layers className="w-3.5 h-3.5 text-[#7A470E]" />
            <span>Executive Academic Catalogue</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-[#0B2644] font-sc">
            Executive Programmes on Offer
          </h2>
          <p className="mt-4 text-base text-slate-600">
            Tailored postgraduate, professional, and diploma degrees designed to accelerate leadership 
            trajectories across Business Administration, Public Administration, and Accounting & Finance.
          </p>

          {/* Filter Pills in Business School Navy and Gold */}
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer font-sc ${
                  selectedCategory === cat
                    ? 'bg-[#0B2644] text-white shadow-md border-2 border-[#7A470E]'
                    : 'bg-white text-slate-700 hover:bg-amber-50 hover:text-[#0B2644] border border-slate-200 shadow-sm'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Programme Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredProgrammes.map((prog) => {
            const isDoctoral = prog.category === 'Doctoral';
            const isMaster = prog.category === "Master's";

            return (
              <div 
                key={prog.id}
                className="translucent-card-light rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-300 flex flex-col justify-between overflow-hidden group hover:border-[#7A470E] hover:scale-[1.01]"
              >
                <div>
                  {/* Top Department Badge & Degree Tag */}
                  <div className="p-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[#7A470E] block mb-1 font-sc">
                        {prog.departmentName}
                      </span>
                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#0B2644] transition-colors font-sc leading-snug">
                        {prog.name}
                      </h3>
                    </div>
                    <span className={`px-2.5 py-1 text-xs font-black rounded-lg shrink-0 font-sc ${
                      isDoctoral 
                        ? 'bg-[#0B2644] text-white' 
                        : isMaster 
                        ? 'bg-amber-100 text-[#5C3306]'
                        : 'bg-slate-100 text-slate-700'
                    }`}>
                      {prog.degree}
                    </span>
                  </div>

                  {/* Body Content */}
                  <div className="p-6 pt-4 space-y-4">
                    <p className="text-xs sm:text-sm text-slate-600 line-clamp-3 leading-relaxed">
                      {prog.description}
                    </p>

                    <div className="space-y-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                      <div className="flex flex-wrap items-baseline justify-between gap-1">
                        <span className="flex items-center text-slate-600 shrink-0">
                          <Clock className="w-3.5 h-3.5 mr-1.5 text-[#7A470E]" />
                          <span>Duration:</span>
                        </span>
                        <strong className="text-slate-800 font-mono font-bold text-right">{prog.duration}</strong>
                      </div>

                      <div className="flex flex-wrap items-baseline justify-between gap-1">
                        <span className="flex items-center text-slate-600 shrink-0">
                          <Calendar className="w-3.5 h-3.5 mr-1.5 text-[#0B2644]" />
                          <span>Lecture Mode:</span>
                        </span>
                        <strong className="text-slate-800 text-right">{prog.mode}</strong>
                      </div>

                      <div className="flex flex-wrap items-baseline justify-between gap-1">
                        <span className="flex items-center text-slate-600 shrink-0">
                          <NairaSign className="w-3.5 h-3.5 mr-1.5 text-[#7A470E]" />
                          <span>Tuition Benchmark:</span>
                        </span>
                        <strong className="text-[#0B2644] font-mono font-black text-right">{prog.feePerSession} / session</strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Footer Actions */}
                <div className="p-6 pt-3 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between gap-3">
                  <button
                    onClick={() => handleOpenDedicatedPage(prog)}
                    className="text-xs font-bold text-[#0B2644] hover:text-[#7A470E] transition-colors flex items-center space-x-1 cursor-pointer font-sc group"
                  >
                    <span>View Dedicated Page</span>
                    <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </button>

                  <button
                    onClick={() => setActiveView('apply')}
                    className="px-4 py-2 bg-[#7A470E] hover:bg-[#5C3306] text-white text-xs font-black rounded-xl transition-all shadow-xs hover:shadow-md cursor-pointer font-sc flex items-center space-x-1"
                  >
                    <span>Apply Now</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* Programme Detail Modal */}
      {selectedProgrammeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto shadow-2xl border-4 border-[#7A470E] relative">
            <button 
              onClick={() => setSelectedProgrammeModal(null)}
              className="absolute top-5 right-5 p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="pr-6">
              <span className="text-xs font-bold uppercase tracking-wider text-[#7A470E] font-sc block">
                {selectedProgrammeModal.departmentName}
              </span>
              <h3 className="text-2xl font-black font-sc text-[#0B2644] mt-1">
                {selectedProgrammeModal.name}
              </h3>
              <div className="flex items-center space-x-2 mt-2">
                <span className="bg-[#0B2644] text-white text-xs font-bold px-2.5 py-0.5 rounded font-sc">
                  {selectedProgrammeModal.degree}
                </span>
                <span className="text-xs text-slate-500">
                  {selectedProgrammeModal.duration} · {selectedProgrammeModal.mode}
                </span>
              </div>
            </div>

            <div className="mt-6 space-y-4 text-xs sm:text-sm text-slate-700">
              <div>
                <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs font-sc mb-1 text-[#0B2644]">
                  Programme Description
                </h4>
                <p className="leading-relaxed">
                  {selectedProgrammeModal.description}
                </p>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs font-sc mb-1 text-[#7A470E]">
                  Admission Requirements
                </h4>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-slate-800 leading-relaxed">
                  {selectedProgrammeModal.requirements}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                  <span className="text-[11px] text-slate-500 block">Tuition per Academic Session:</span>
                  <span className="font-mono text-base font-bold text-[#0B2644]">
                    {selectedProgrammeModal.feePerSession}
                  </span>
                </div>
                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                  <span className="text-[11px] text-slate-500 block">Registration & Application Fee:</span>
                  <span className="font-mono text-base font-bold text-[#7A470E]">
                    ₦30,000 (Non-Refundable)
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-end space-x-3">
              <button
                onClick={() => setSelectedProgrammeModal(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer font-sc"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const currentProg = selectedProgrammeModal;
                  setSelectedProgrammeModal(null);
                  handleOpenDedicatedPage(currentProg);
                }}
                className="px-4 py-2.5 bg-[#0B2644] hover:bg-[#06182B] text-white text-xs font-bold rounded-xl transition-all cursor-pointer font-sc flex items-center space-x-1.5 border border-[#7A470E]/40"
              >
                <BookOpen className="w-3.5 h-3.5 text-amber-200" />
                <span>Dedicated Programme Page</span>
              </button>
              <button
                onClick={() => {
                  setSelectedProgrammeModal(null);
                  setActiveView('apply');
                }}
                className="px-5 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white text-xs font-black rounded-xl shadow-md transition-all cursor-pointer font-sc"
              >
                Apply Online (₦25,000)
              </button>
            </div>

          </div>
        </div>
      )}

    </section>
  );
};
